import { useState, useEffect } from "react";
import { BsStarFill, BsStar } from "react-icons/bs";
import { RiVerifiedBadgeFill } from "react-icons/ri";
import { AiOutlineLeft, AiOutlineRight, AiOutlineClose } from "react-icons/ai";

const ReviewList = ({ 
  reviews, 
  currentPage, 
  totalPages, 
  onPageChange,
  onOpenReviewForm 
}) => {
  const [reviewImageModal, setReviewImageModal] = useState({
    open: false,
    images: [],
    currentIndex: 0,
    review: null,
  });

  useEffect(() => {
    console.log("reviews", reviews);
  }, [reviews]);

  const openReviewImageModal = (images, index, review) => {
    setReviewImageModal({
      open: true,
      images,
      currentIndex: index,
      review,
    });
  };

  const closeReviewImageModal = () => {
    setReviewImageModal({
      open: false,
      images: [],
      currentIndex: 0,
      review: null,
    });
  };

  const navigateReviewImage = (direction) => {
    const { images, currentIndex } = reviewImageModal;
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    }
    
    setReviewImageModal(prev => ({
      ...prev,
      currentIndex: newIndex,
    }));
  };

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 mb-4">No reviews yet.</p>
        <button
          onClick={onOpenReviewForm}
          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
        >
          Be the first to review
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 mb-6 mt-4">
        {reviews.map((review) => (
          <div
            key={review._id}
            className="border-b pb-6 last:border-b-0"
          >
            <div className="flex justify-between mb-2">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-lg font-medium mr-3">
                  {review.userId?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <div className="font-medium">{review.userId?.name || 'User'}</div>
                  {review.verified && (
                    <div className="flex items-center text-xs text-green-600">
                      <RiVerifiedBadgeFill className="mr-1" />
                      <span>Verified Purchase</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {new Date(review.createdAt).toLocaleDateString()}
              </div>
            </div>

            <div className="flex mb-2">
              {[...Array(5)].map((_, i) =>
                i < review.ratings ? (
                  <BsStarFill key={i} className="text-yellow-400" />
                ) : (
                  <BsStar key={i} className="text-yellow-400" />
                )
              )}
            </div>

            <p className="text-gray-600 mb-3">{review.reviews}</p>

            {review.productImage && review.productImage.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {review.productImage.map((image, idx) => (
                  <button
                    key={idx}
                    onClick={() => openReviewImageModal(review.productImage, idx, review)}
                    className="w-20 h-20 hover:opacity-80 transition-opacity flex items-center justify-center bg-gray-100 rounded-md overflow-hidden"
                  >
                    <img 
                      src={image} 
                      alt={`Review image ${idx + 1}`}
                      className="w-full h-full object-cover"
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {reviewImageModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-4xl">
            <div className="flex justify-end mb-2">
              <button
                onClick={closeReviewImageModal}
                className="text-white hover:text-gray-300 p-2"
              >
                <AiOutlineClose size={24} />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={() => navigateReviewImage('prev')}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 z-10"
              >
                <AiOutlineLeft size={24} />
              </button>

              <div className="flex justify-center items-center h-[65vh]">
                <img
                  src={reviewImageModal.images[reviewImageModal.currentIndex]}
                  alt={`Review image ${reviewImageModal.currentIndex + 1}`}
                  className="max-h-full max-w-full object-contain rounded-lg"
                  style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%' }}
                />
              </div>

              <button
                onClick={() => navigateReviewImage('next')}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 z-10"
              >
                <AiOutlineRight size={24} />
              </button>
            </div>

            <div className="mt-4 text-white text-center">
              <p className="text-sm">
                {reviewImageModal.currentIndex + 1} of {reviewImageModal.images.length}
              </p>
            </div>

            {reviewImageModal.review && (
              <div className="mt-6 bg-gray-800 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-lg font-medium text-white mr-3">
                    {reviewImageModal.review.userId?.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div className="font-medium text-white">
                      {reviewImageModal.review.userId?.name || 'User'}
                    </div>
                    {reviewImageModal.review.verified && (
                      <div className="flex items-center text-xs text-green-400">
                        <RiVerifiedBadgeFill className="mr-1" />
                        <span>Verified Purchase</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex mb-2">
                  {[...Array(5)].map((_, i) =>
                    i < reviewImageModal.review.ratings ? (
                      <BsStarFill key={i} className="text-yellow-400" />
                    ) : (
                      <BsStar key={i} className="text-yellow-400" />
                    )
                  )}
                </div>

                <p className="text-gray-300">
                  {reviewImageModal.review.reviews}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-center mt-6">
        <nav className="inline-flex rounded-md shadow">
          <button
            onClick={() => onPageChange(currentPage > 1 ? currentPage - 1 : 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(
            (number) => (
              <button
                key={number}
                onClick={() => onPageChange(number)}
                className={`px-3 py-1 border-t border-b border-gray-300 text-sm font-medium ${
                  currentPage === number
                    ? "bg-blue-50 text-blue-600 border-blue-500"
                    : "bg-white text-gray-500 hover:bg-gray-50"
                }`}
              >
                {number}
              </button>
            )
          )}
          <button
            onClick={() => onPageChange(
              currentPage < totalPages ? currentPage + 1 : totalPages
            )}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </nav>
      </div>
    </>
  );
};

export default ReviewList;